import React, { useRef } from 'react';

import Input from '../Input';
import {
  SearchBarRoot,
  InputArea,
  SearchBarDivider,
  ActionArea,
  SearchIconButton,
} from './styles';

/**
 * SearchBar — barra de busca genérica do design system.
 *
 * Composição: `Input` (formato pill, com lupa) + divisória tracejada opcional
 * + um slot de ação (`action`). NÃO amarra nenhuma ação específica: o botão
 * "Selecionar todos" (ou qualquer outro) é passado via `action`.
 *
 * A busca é disparada por Enter no campo ou clique na lupa, chamando
 * `onSearch(value)`. Pode ser controlado (`value`/`onChange`) ou não.
 *
 * @example
 * <SearchBar
 *   placeholder="Buscar por"
 *   onSearch={(q) => filtrar(q)}
 *   action={<Button variant="secondary" size="xl" shape="pill">Selecionar todos</Button>}
 * />
 */
export type SearchBarProps = {
  /** Valor (modo controlado) */
  value?: string;
  /** Valor inicial (modo não-controlado) */
  defaultValue?: string;
  /** Mudança do texto */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Disparado no Enter ou no clique da lupa, com o valor atual */
  onSearch?: (value: string) => void;
  /** Placeholder do campo @default 'Buscar por' */
  placeholder?: string;
  /** Slot de ação à direita (ex.: botão). Mostra a divisória quando presente. */
  action?: React.ReactNode;
  /** Desabilita o campo e a lupa */
  disabled?: boolean;
  /** Nome acessível do campo @default 'Buscar' */
  'aria-label'?: string;
  /** Classe no container */
  className?: string;
};

const MagnifierIcon = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path
      d="M16.5 16.5L21 21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const SearchBar = ({
  value,
  defaultValue,
  onChange,
  onSearch,
  placeholder = 'Buscar por',
  action,
  disabled = false,
  'aria-label': ariaLabel = 'Buscar',
  className,
}: SearchBarProps): React.ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerSearch = (): void => {
    if (onSearch) onSearch(inputRef.current?.value ?? '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onSearch) onSearch(e.currentTarget.value);
    }
  };

  const trailingIcon = onSearch ? (
    <SearchIconButton
      type="button"
      aria-label="Buscar"
      onClick={triggerSearch}
      disabled={disabled}
    >
      <MagnifierIcon />
    </SearchIconButton>
  ) : (
    <MagnifierIcon />
  );

  return (
    <SearchBarRoot className={className}>
      <InputArea>
        <Input
          ref={inputRef}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onKeyDown={onSearch ? handleKeyDown : undefined}
          placeholder={placeholder}
          disabled={disabled}
          fullWidth
          size="xl"
          shape="pill"
          aria-label={ariaLabel}
          trailingIcon={trailingIcon}
        />
      </InputArea>

      {action && <SearchBarDivider aria-hidden="true" />}
      {action && <ActionArea>{action}</ActionArea>}
    </SearchBarRoot>
  );
};

export default SearchBar;
